import React, { useRef } from 'react';
import BottomSheet from '../../../ui/BottomSheet';
import CardImage from '../../../ui/CardImage';
import { cardImageProps } from '../utils/cardView';
import { useActionConfirm } from '../hooks/useActionConfirm';
import { ActionList } from './ActionSheet';
import CardValues from './CardValues';
import './CardActionSheet.css';

/**
 * Langdruck-Sheet am Handy (Spec 2026-10-21 §5.2, §5.4): die Karte groß, daneben bzw. darunter
 * ihre Aktionen — dieselbe Liste, die am Rechner das Kontextmenü zeigt.
 *
 *   <CardActionSheet open={!!sheetCard} onOpenChange={(o) => { if (!o) setSheetCard(null); }}
 *     card={sheetCard} actions={battlefieldCardActions(sheetCard, ctx)} />
 *
 * - Wann das Sheet aufgeht, entscheidet die Seite (Langdruck über `usePlaytestDnd`).
 * - Das Bild startet mit der echten Seite der Karte (`defaultFace`); der Flip-Knopf dreht nur
 *   zum Lesen, der Server-Zustand bleibt unberührt. Eine fremde verdeckte Karte maskiert der Server
 *   (`scryfallData.imageUrl === 'card-back'`): sie steht als Kartenrücken mit dem Titel
 *   „Verdeckte Karte". Die eigene verdeckte Karte zeigt ihre Vorderseite, der Titel nennt
 *   „(verdeckt)" — ihr Besitzer kennt sie und will lesen, was er gleich aufdeckt.
 * - Zwischen Karte und Aktionen steht, sofern vorhanden, die Werte-Zeile (`sheet-values`:
 *   Stärke/Widerstandskraft, Counter, Marken; Spec §14). Sie steht über der Liste in deren Spalte:
 *   quer füllt die Karte schon die ganze Sheet-Höhe, unter ihr bliebe die Zeile unsichtbar.
 * - Quer (Handy-Brett) stehen Karte und Liste nebeneinander, hochkant untereinander.
 * - Ein Tipp schließt das Sheet und führt den Eintrag aus; `children` werden zu Abschnitten.
 * - Wird `card` beim Schließen null, bleiben Karte und Liste stehen, bis die Schließ-Animation
 *   des Sheets vorbei ist.
 */
export default function CardActionSheet({ open, onOpenChange, card, actions = [] }) {
  const { select, dialog } = useActionConfirm();
  const lastRef = useRef(null);
  if (card) lastRef.current = { card, actions };
  const shown = card ? { card, actions } : lastRef.current;
  if (!shown) return dialog;
  const { face, ...imageProps } = cardImageProps(shown.card);
  const masked = shown.card.scryfallData?.imageUrl === 'card-back';
  const title = masked ? 'Verdeckte Karte' : `${imageProps.alt}${shown.card.faceDown ? ' (verdeckt)' : ''}`;

  return (
    <>
      <BottomSheet open={open && !!card} onOpenChange={onOpenChange} title={title} size="tall">
        <div className="card-action-sheet">
          <div className="card-action-sheet__card" data-testid="card-action-sheet-card" data-instance-id={shown.card.instanceId}>
            {masked ? <CardImage.Back label="Verdeckte Karte" /> : <CardImage {...imageProps} defaultFace={face} />}
          </div>
          <div className="card-action-sheet__actions">
            <CardValues card={shown.card} variant="row" className="card-action-sheet__values" data-testid="sheet-values" />
            <ActionList
              actions={shown.actions}
              onSelect={(entry) => {
                onOpenChange?.(false);
                select(entry);
              }}
            />
          </div>
        </div>
      </BottomSheet>
      {dialog}
    </>
  );
}
